import { useEffect, useState } from "react";
import {
  getAllPromoters, createPromoter, updatePromoter, sendPasswordReset,
  archivePromoter, restorePromoter,
  getAllSupervisors, createSupervisor, getSupervisorAssignment, setSupervisorAssignment,
} from "../../firebase/services";
import { useLanguage } from "../../hooks/useLanguage";
import { isProbationMonth, upsertSalaryHistory, salaryForMonth } from "../../firebase/schema";
import { format } from "date-fns";

const EMPTY_PROMOTER = { name: "", email: "", password: "", shopName: "", baseSalary: "", allowance: "", startDate: "", dependents: "", salaryFrom: "" };
const EMPTY_SUPERVISOR = { name: "", email: "", password: "" };

export default function AdminPromoters() {
  const { t } = useLanguage();
  const [mainTab, setMainTab] = useState("promoters");

  // ── Promoter state ──
  const [promoters, setPromoters] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_PROMOTER);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // ── Supervisor state ──
  const [supervisors, setSupervisors] = useState([]);
  const [showSvForm, setShowSvForm] = useState(false);
  const [svForm, setSvForm] = useState(EMPTY_SUPERVISOR);
  const [svSaving, setSvSaving] = useState(false);
  const [svMsg, setSvMsg] = useState("");
  // Assign panel
  const [assignSv, setAssignSv] = useState(null);       // supervisor đang chọn assign
  const [assignedIds, setAssignedIds] = useState([]);    // promoterIds hiện tại
  const [assignSaving, setAssignSaving] = useState(false);

  const loadPromoters = async (archived = showArchived) => setPromoters(await getAllPromoters(archived));
  const loadSupervisors = async () => setSupervisors(await getAllSupervisors());

  useEffect(() => { loadPromoters(); loadSupervisors(); }, []);

  // ── Promoter CRUD ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Áp dụng lùi về tháng đã qua → bảng lương các tháng đó sẽ tính lại theo mức mới
    if (editId && salaryChanged && (form.salaryFrom || thisMonth) < thisMonth
      && !window.confirm(t("prom.salary_past_warn", { month: form.salaryFrom }))) return;
    setSaving(true);
    try {
      if (editId) {
        const p = promoters.find(x => x.uid === editId) || {};
        const data = {
          name: form.name, shopName: form.shopName,
          startDate: form.startDate || null, dependents: Number(form.dependents) || 0,
        };
        if (salaryChanged) {
          // Lương mới chỉ áp dụng từ tháng chọn; các tháng trước giữ mức cũ
          const salaryHistory = upsertSalaryHistory(p, {
            effectiveFrom: form.salaryFrom || thisMonth,
            baseSalary: form.baseSalary, allowance: form.allowance,
          });
          const now = salaryForMonth({ salaryHistory }, thisMonth);
          Object.assign(data, { salaryHistory, baseSalary: now.baseSalary, allowance: now.allowance });
        }
        await updatePromoter(editId, data);
        setMsg(salaryChanged ? t("prom.salary_saved", { month: form.salaryFrom || thisMonth }) : "Đã cập nhật thành công");
      } else {
        await createPromoter(form);
        setMsg("Đã tạo tài khoản thành công");
      }
      setForm(EMPTY_PROMOTER); setShowForm(false); setEditId(null); loadPromoters();
    } catch (err) { setMsg("Lỗi: " + err.message); }
    finally { setSaving(false); setTimeout(() => setMsg(""), 3000); }
  };

  const handleSendReset = async (p) => {
    if (!window.confirm(`Gửi email đặt lại mật khẩu đến ${p.email}?`)) return;
    try { await sendPasswordReset(p.email); setMsg(`✅ Đã gửi email đến ${p.email}`); }
    catch (err) { setMsg("Lỗi: " + err.message); }
    setTimeout(() => setMsg(""), 4000);
  };

  const handleArchive = async (p) => {
    if (!window.confirm(t("prom.confirm_archive"))) return;
    try { await archivePromoter(p.uid); setMsg(`✅ Đã lưu trữ ${p.name}`); loadPromoters(showArchived); }
    catch (err) { setMsg("Lỗi: " + err.message); }
    setTimeout(() => setMsg(""), 3000);
  };

  const handleRestore = async (p) => {
    try { await restorePromoter(p.uid); setMsg(`✅ Đã khôi phục ${p.name}`); loadPromoters(showArchived); }
    catch (err) { setMsg("Lỗi: " + err.message); }
    setTimeout(() => setMsg(""), 3000);
  };

  const toggleShowArchived = (val) => {
    setShowArchived(val);
    loadPromoters(val);
  };

  const thisMonth = format(new Date(), "yyyy-MM");
  const editing = editId ? promoters.find(x => x.uid === editId) : null;
  // Mức lương đang áp dụng tháng này (mốc so sánh để biết admin có đổi lương không)
  const editingNow = editing ? salaryForMonth(editing, thisMonth) : null;
  const salaryChanged = !!editing && (Number(form.baseSalary) !== editingNow.baseSalary
    || (Number(form.allowance) || 0) !== editingNow.allowance);

  const startEdit = (p) => {
    const now = salaryForMonth(p, thisMonth);
    setEditId(p.uid);
    setForm({ name: p.name, email: p.email, shopName: p.shopName, baseSalary: now.baseSalary,
      allowance: now.allowance || "", password: "",
      startDate: p.startDate || "", dependents: p.dependents ?? "", salaryFrom: thisMonth });
    setShowForm(true);
  };

  // ── Supervisor CRUD ──
  const handleCreateSv = async (e) => {
    e.preventDefault(); setSvSaving(true);
    try {
      await createSupervisor(svForm);
      setSvMsg("Đã tạo supervisor thành công");
      setSvForm(EMPTY_SUPERVISOR); setShowSvForm(false); loadSupervisors();
    } catch (err) { setSvMsg("Lỗi: " + err.message); }
    finally { setSvSaving(false); setTimeout(() => setSvMsg(""), 3000); }
  };

  // ── Assign promoters ──
  const openAssign = async (sv) => {
    setAssignSv(sv);
    const ids = await getSupervisorAssignment(sv.uid);
    setAssignedIds(ids);
  };

  const toggleAssign = (uid) =>
    setAssignedIds(ids => ids.includes(uid) ? ids.filter(x => x !== uid) : [...ids, uid]);

  const saveAssign = async () => {
    setAssignSaving(true);
    await setSupervisorAssignment(assignSv.uid, assignedIds);
    setAssignSaving(false); setAssignSv(null);
    setSvMsg(`Đã cập nhật nhóm cho ${assignSv.name}`);
    setTimeout(() => setSvMsg(""), 3000);
  };

  return (
    <>
      <div className="page-header">
        <div><h1>{t("prom.title")}</h1><p>{t("prom.subtitle")}</p></div>
        {mainTab === "promoters"
          ? <button className="btn btn-primary" onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY_PROMOTER); }}> {t("prom.add_promoter")}</button>
          : <button className="btn btn-primary" onClick={() => { setShowSvForm(true); setSvForm(EMPTY_SUPERVISOR); }}> {t("prom.add_supervisor")}</button>
        }
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        <div className={"tab" + (mainTab === "promoters" ? " active" : "")} onClick={() => setMainTab("promoters")}>{t("prom.tab_promoters")}</div>
        <div className={"tab" + (mainTab === "supervisors" ? " active" : "")} onClick={() => setMainTab("supervisors")}> {t("prom.tab_supervisors")}</div>
      </div>

      {/* ── PROMOTERS TAB ── */}
      {mainTab === "promoters" && (
        <>
          {msg && <div className={`alert ${msg.startsWith("Lỗi") ? "alert-error" : "alert-success"}`}>{msg}</div>}
          {showForm && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <div className="card-title">{editId ? t("prom.edit_title") : t("prom.create_title")}</div>
                <button className="btn btn-sm" onClick={() => setShowForm(false)}>✕</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="grid-2">
                  <div className="form-group"><label className="form-label">{t("common.name")}</label>
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></div>
                  <div className="form-group"><label className="form-label">{t("common.email")}</label>
                    <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} disabled={!!editId} required={!editId} /></div>
                  {!editId && <div className="form-group"><label className="form-label">Mật khẩu</label>
                    <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required /></div>}
                  <div className="form-group"><label className="form-label">{t("prom.shop_name")}</label>
                    <input value={form.shopName} onChange={e => setForm(f => ({ ...f, shopName: e.target.value }))} required /></div>
                  <div className="form-group"><label className="form-label">{t("prom.base_salary")}</label>
                    <input type="number" value={form.baseSalary} onChange={e => setForm(f => ({ ...f, baseSalary: e.target.value }))} required /></div>
                  <div className="form-group"><label className="form-label">{t("salary.allowance")} (đ)</label>
                    <input type="number" min="0" value={form.allowance} onChange={e => setForm(f => ({ ...f, allowance: e.target.value }))} placeholder="0" /></div>
                  {editId && salaryChanged && (
                    <div className="form-group" style={{ gridColumn: "1 / -1", background: "#FFF7E6", padding: 10, borderRadius: 8 }}>
                      <label className="form-label">📅 {t("prom.salary_from")}</label>
                      <input type="month" value={form.salaryFrom} onChange={e => setForm(f => ({ ...f, salaryFrom: e.target.value }))} required style={{ maxWidth: 200 }} />
                      <div style={{ fontSize: 11, color: "#854F0B", marginTop: 4 }}>
                        {t("prom.salary_from_note", { old: Number(editingNow.baseSalary).toLocaleString("vi-VN") })}
                      </div>
                    </div>
                  )}
                  {editId && Array.isArray(editing?.salaryHistory) && editing.salaryHistory.length > 1 && (
                    <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                      <label className="form-label">{t("prom.salary_history")}</label>
                      <table style={{ fontSize: 12 }}><tbody>
                        {[...editing.salaryHistory].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom)).map(h => (
                          <tr key={h.effectiveFrom}>
                            <td style={{ padding: "2px 12px 2px 0", color: "#666" }}>
                              {h.effectiveFrom === "2000-01" ? t("prom.salary_initial") : t("prom.salary_from_m", { m: h.effectiveFrom })}
                            </td>
                            <td style={{ padding: "2px 12px 2px 0" }}>{Number(h.baseSalary).toLocaleString("vi-VN")} đ</td>
                            <td style={{ padding: "2px 0", color: "#888" }}>{h.allowance > 0 ? `+ ${Number(h.allowance).toLocaleString("vi-VN")} đ ${t("salary.allowance").toLowerCase()}` : ""}</td>
                          </tr>
                        ))}
                      </tbody></table>
                    </div>
                  )}
                  <div className="form-group"><label className="form-label">{t("prom.start_date")}</label>
                    <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
                    <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>{t("prom.start_date_note")}</div></div>
                  <div className="form-group"><label className="form-label">{t("prom.dependents")}</label>
                    <input type="number" min="0" value={form.dependents} onChange={e => setForm(f => ({ ...f, dependents: e.target.value }))} placeholder="0" />
                    <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>{t("prom.dependents_note")}</div></div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? t("common.saving") : editId ? t("prom.save_btn") : t("prom.create_btn")}</button>
                  <button className="btn" type="button" onClick={() => setShowForm(false)}>{t("common.cancel")}</button>
                </div>
              </form>
            </div>
          )}
          <div className="card">
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#666", cursor: "pointer" }}>
                <input type="checkbox" checked={showArchived} onChange={e => toggleShowArchived(e.target.checked)} />
                {t("prom.show_archived")}
              </label>
            </div>
            <table className="table-card">
              <thead><tr><th>{t("common.name")}</th><th>{t("sales.shop")}</th><th>{t("common.email")}</th><th>{t("prom.base_salary")}</th><th>{t("salary.allowance")}</th><th>{t("prom.start_date")}</th><th>{t("prom.dependents")}</th><th></th></tr></thead>
              <tbody>
                {promoters.map(p => (
                  <tr key={p.uid} style={p.active === false ? { opacity: 0.6 } : undefined}>
                    <td data-label="Họ tên">
                      <strong>{p.name}</strong>
                      {p.active === false && <span className="badge badge-gray" style={{ marginLeft: 6 }}>{t("prom.archived_badge")}</span>}
                    </td>
                    <td data-label="Shop">{p.shopName}</td>
                    <td data-label="Email" style={{ color: "#888" }}>{p.email}</td>
                    <td data-label="Lương cơ bản">
                      {Number(salaryForMonth(p, thisMonth).baseSalary).toLocaleString("vi-VN")} đ
                      {(p.salaryHistory || []).filter(h => h.effectiveFrom > thisMonth).map(h => (
                        <div key={h.effectiveFrom} style={{ fontSize: 11, color: "#B45309" }}>
                          → {Number(h.baseSalary).toLocaleString("vi-VN")} đ {t("prom.salary_from_m", { m: h.effectiveFrom })}
                        </div>
                      ))}
                    </td>
                    <td data-label="Phụ cấp">{salaryForMonth(p, thisMonth).allowance > 0 ? Number(salaryForMonth(p, thisMonth).allowance).toLocaleString("vi-VN") + " đ" : "—"}</td>
                    <td data-label="Ngày vào làm">
                      {p.startDate
                        ? <>
                            {p.startDate.split("-").reverse().join("/")}
                            {isProbationMonth(p.startDate, format(new Date(), "yyyy-MM")) &&
                              <span className="badge badge-amber" style={{ marginLeft: 6 }}>{t("prom.probation")}</span>}
                          </>
                        : <span style={{ color: "#C8152A" }} title={t("prom.no_start_date")}>⚠ —</span>}
                    </td>
                    <td data-label="Người phụ thuộc">{p.dependents > 0 ? p.dependents : "—"}</td>
                    <td data-label=""><div style={{ display: "flex", gap: 6 }}>
                      {p.active !== false ? (
                        <>
                          <button className="btn btn-sm" onClick={() => startEdit(p)}>{t("common.edit")}</button>
                          <button className="btn btn-sm" onClick={() => handleSendReset(p)}>{t("prom.reset_pw")}</button>
                          <button className="btn btn-sm" style={{ color: "#b45309", borderColor: "#fde68a" }} onClick={() => handleArchive(p)}>{t("prom.archive")}</button>
                        </>
                      ) : (
                        <button className="btn btn-sm btn-primary" onClick={() => handleRestore(p)}>{t("prom.restore")}</button>
                      )}
                    </div></td>
                  </tr>
                ))}
                {promoters.length === 0 && <tr><td colSpan={6} style={{ color: "#aaa", textAlign: "center", padding: 24 }}>{t("prom.no_promoters")}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── SUPERVISORS TAB ── */}
      {mainTab === "supervisors" && (
        <>
          {svMsg && <div className={`alert ${svMsg.startsWith("Lỗi") ? "alert-error" : "alert-success"}`}>{svMsg}</div>}
          {showSvForm && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <div className="card-title">{t("prom.add_supervisor")}</div>
                <button className="btn btn-sm" onClick={() => setShowSvForm(false)}>✕</button>
              </div>
              <form onSubmit={handleCreateSv}>
                <div className="grid-2">
                  <div className="form-group"><label className="form-label">{t("common.name")}</label>
                    <input value={svForm.name} onChange={e => setSvForm(f => ({ ...f, name: e.target.value }))} required /></div>
                  <div className="form-group"><label className="form-label">{t("common.email")}</label>
                    <input type="email" value={svForm.email} onChange={e => setSvForm(f => ({ ...f, email: e.target.value }))} required /></div>
                  <div className="form-group"><label className="form-label">Mật khẩu</label>
                    <input type="password" value={svForm.password} onChange={e => setSvForm(f => ({ ...f, password: e.target.value }))} required /></div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn btn-primary" type="submit" disabled={svSaving}>{svSaving ? t("common.saving") : t("prom.create_btn")}</button>
                  <button className="btn" type="button" onClick={() => setShowSvForm(false)}>{t("common.cancel")}</button>
                </div>
              </form>
            </div>
          )}

          <div className="card">
            <table className="table-card">
              <thead><tr><th>{t("common.name")}</th><th>{t("common.email")}</th><th></th></tr></thead>
              <tbody>
                {supervisors.map(sv => (
                  <tr key={sv.uid}>
                    <td data-label="Họ tên"><strong>{sv.name}</strong></td>
                    <td data-label="Email" style={{ color: "#888" }}>{sv.email}</td>
                    <td data-label=""><div style={{ display: "flex", gap: 6 }}>
                      <button className="btn btn-sm btn-primary" onClick={() => openAssign(sv)}>{t("prom.assign")}</button>
                      <button className="btn btn-sm" onClick={() => { sendPasswordReset(sv.email); setSvMsg(`✅ Đã gửi email reset đến ${sv.email}`); setTimeout(() => setSvMsg(""), 3000); }}>{t("prom.reset_pw")}</button>
                    </div></td>
                  </tr>
                ))}
                {supervisors.length === 0 && <tr><td colSpan={3} style={{ color: "#aaa", textAlign: "center", padding: 24 }}>{t("prom.no_supervisors")}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── ASSIGN MODAL ── */}
      {assignSv && (
        <div className="modal-overlay" onClick={() => setAssignSv(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <span className="modal-title">Assign promoter → {assignSv.name}</span>
              <button className="modal-close" onClick={() => setAssignSv(null)}>✕</button>
            </div>
            <p style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>{t("prom.assign_hint")}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 300, overflowY: "auto" }}>
              {promoters.map(p => (
                <label key={p.uid} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "8px 12px", borderRadius: 8, background: assignedIds.includes(p.uid) ? "#eff6ff" : "#fafafa", border: "1px solid " + (assignedIds.includes(p.uid) ? "#bfdbfe" : "#e5e7eb") }}>
                  <input type="checkbox" checked={assignedIds.includes(p.uid)} onChange={() => toggleAssign(p.uid)} />
                  <div>
                    <div style={{ fontWeight: 500 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: "#888" }}>{p.shopName}</div>
                  </div>
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={saveAssign} disabled={assignSaving}>
                {assignSaving ? t("common.saving") : t("prom.save_btn")}
              </button>
              <button className="btn" onClick={() => setAssignSv(null)}>{t("common.cancel")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
